import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import http from 'hub-http/clients/apiClient';
import { undoDepth } from 'prosemirror-history';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import Raven from 'raven-js';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import { emailReplyHistoryClass } from '../ProsemirrorSchema';
import { getDocNodeFromHtml } from '../ProsemirrorUtils';
import { findNode } from '../utils/nodeUtils';
import uuid from '../utils/uuid';
import { jsx as _jsx } from "react/jsx-runtime";
class EmailReplyHistoryView {
  constructor(node) {
    this.dom = this.contentDOM = document.createElement('div');
    this.dom.classList.add(emailReplyHistoryClass);
    if (node.attrs.hidden) {
      this.dom.classList.add('hidden');
    }
    if (node.attrs.pristine) {
      this.dom.setAttribute('data-pristine', 'true');
    }
  }
  update(node) {
    if (node.type.name !== 'emailReplyHistory') return false;
    if (node.attrs.hidden) {
      this.dom.classList.add('hidden');
    } else {
      this.dom.classList.remove('hidden');
    }
    if (node.attrs.pristine) {
      this.dom.setAttribute('data-pristine', 'true');
    } else {
      this.dom.removeAttribute('data-pristine');
    }
    return true;
  }
}
export function createEmailReplyHistoryPlugin(pluginOptions) {
  const key = new PluginKey(`emailReplyHistory_${uuid()}`);
  const EmailReplyHistoryPlugin = new Plugin({
    key,
    appendTransaction(_trs, oldState, newState) {
      if (wasModified()) {
        const hNode = findNode(newState.doc, n => n.type.name === 'emailReplyHistory');
        if (!hNode || !hNode.node.attrs.pristine) return;
        return newState.tr.setNodeMarkup(hNode.pos, undefined, Object.assign({}, hNode.node.attrs, {
          pristine: false
        })).setMeta('addToHistory', false);
      }
      function wasModified() {
        return undoDepth(oldState) === 0 && undoDepth(newState) === 1;
      }
    },
    props: {
      decorations(state) {
        const historyNode = findNode(state.doc, n => n.type.name === 'emailReplyHistory');
        if (!historyNode) {
          Raven.captureException(new Error('EmailReplyHistory node not found'));
          return null;
        }
        const dom = document.createElement('div');
        const root = createRoot(dom);
        return DecorationSet.create(state.doc, [Decoration.widget(historyNode.pos, (view, getPos) => {
          root.render( /*#__PURE__*/_jsx(ToggleButton, {
            active: !historyNode.node.attrs.hidden,
            onClick: () => toggleVisibility(view, getPos)
          }));
          return dom;
        }, {
          destroy() {
            window.requestIdleCallback(() => root.unmount());
          },
          ignoreSelection: true,
          stopEvent() {
            return true;
          }
        })]);
        function toggleVisibility(view, getPos) {
          const hNode = view.state.doc.nodeAt(getPos());
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), undefined, Object.assign({}, hNode.attrs, {
            hidden: !hNode.attrs.hidden
          })).setMeta('addToHistory', false));
        }
      },
      nodeViews: {
        emailReplyHistory(node, view, getPos) {
          insertReplyHistory(node, view, getPos, pluginOptions);
          return new EmailReplyHistoryView(node);
        }
      }
    }
  });
  return EmailReplyHistoryPlugin;
}
const StyledIconButton = styled(UIIconButton).attrs(() => ({
  shape: 'circle'
})).withConfig({
  displayName: "EmailReplyHistory__StyledIconButton",
  componentId: "bog5iv-0"
})(["&&{width:45px;height:21px;padding-top:6px;border-radius:31px;}"]);
function ToggleButton({
  active,
  onClick
}) {
  return /*#__PURE__*/_jsx(StyledIconButton, {
    active: active,
    onClick: onClick,
    "data-test-id": "EmailReplyHistoryToggleButton",
    children: /*#__PURE__*/_jsx(UIIcon, {
      name: "ellipses",
      size: "medium"
    })
  });
}
function insertReplyHistory(node, view, getPos, pluginOptions) {
  if (!node.attrs.pristine) return {};
  fetchEmailReplyHistory(pluginOptions).then(html => {
    view.dispatch(view.state.tr.replaceWith(getPos(), node.nodeSize, getDocNodeFromHtml(html, {
      preserveNewlines: false
    })).setMeta('addToHistory', false));
  }).catch(e => {
    Raven.captureException(e);
  });
}
function fetchEmailReplyHistory({
  conversationsMessageId,
  inReplyToDisposition
}) {
  return http.get(`/conversations-email/v1/app-messages/${conversationsMessageId}/formatted-reply-body`, {
    query: {
      inReplyToDisposition
    },
    headers: {
      Accept: 'text/html'
    }
  });
}